import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';

const formatTime = (s) => `${Math.floor(Math.max(0,s) / 60)}:${String(Math.floor(Math.max(0,s)) % 60).padStart(2, '0')}`;

export default function MatDisplay() {
  const matNr = parseInt(window.location.pathname.match(/\/display\/(\d+)/)?.[1]) || 1;
  // Turnier aus ?t= — der Server trennt die Matten je Turnier (27.09.2026).
  // Ohne Angabe landet der Schirm im Raum ohne Turnier (wie alte Links).
  const turnierId = Number(new URLSearchParams(window.location.search).get('t')) || null;
  const [state, setState] = useState(null);
  const [connected, setConnected] = useState(false);
  const [showWinner, setShowWinner] = useState(false);
  const [winnerData, setWinnerData] = useState(null);

  useEffect(() => {
    const sock = io('/referee', { auth: { turnierId }, transports: ['websocket', 'polling'], reconnection: true, reconnectionDelay: 1500 });
    sock.on('connect', () => { setConnected(true); sock.emit('join-mat', { matNr, name: 'Display', role: 'display' }); });
    sock.on('disconnect', () => setConnected(false));
    sock.on('mat-state', s => {
      setState(s);
      if (s.status === 'finished' && s.winner) { setWinnerData(s); setShowWinner(true); }
      else setShowWinner(false);
    });
    sock.on('score-update', d => setState(p => p ? { ...p, ...d } : p));
    sock.on('timer-tick', ({ remaining }) => setState(p => p ? { ...p, timer_remaining: remaining, timer_running: true } : p));
    sock.on('timer-update', ({ running, remaining }) => setState(p => p ? { ...p, timer_running: running, timer_remaining: remaining } : p));
    sock.on('timer-ended', () => setState(p => p ? { ...p, timer_running: false } : p));
    sock.on('kata-update', d => setState(p => p ? { ...p, kata_notes: d.kata_notes || {}, kata_average: d.average, kata_label: d.label } : p));
    sock.on('bruchtest-update', d => setState(p => p ? { ...p, bruchtest_rot_scores: d.rot_scores || [], bruchtest_blau_scores: d.blau_scores || [], bruchtest_rot_total: d.rot_total || 0, bruchtest_blau_total: d.blau_total || 0, bruchtest_active_side: d.active_side } : p));
    sock.on('result-confirmed', d => { setState(p => p ? { ...p, status: 'finished', winner: d.winner } : p); setWinnerData(d); setShowWinner(true); });
    sock.on('referees-update', ({ referees }) => setState(p => p ? { ...p, connected_referees: referees } : p));
    return () => sock.disconnect();
  }, [matNr, turnierId]);

  const s = state || { status: 'waiting', session_typ: 'kumite', rote_punkte: 0, blaue_punkte: 0, rote_warnings: 0, blaue_warnings: 0, timer_remaining: 180, timer_running: false, kata_notes: {}, bruchtest_rot_scores: [], bruchtest_blau_scores: [], fight_info: null };
  const isKata = s.session_typ === 'kata';
  const isBruchtest = s.session_typ === 'bruchtest';
  const isKumite = !isKata && !isBruchtest;
  const timeRed = s.timer_remaining <= 30;
  const timeYellow = !timeRed && s.timer_remaining <= 60;

  const modeLabel = isKata ? 'KATA' : isBruchtest ? 'BRUCHTEST' : 'KUMITE';
  const rotTotal = s.bruchtest_rot_total || (s.bruchtest_rot_scores || []).reduce((a, e) => a + (e.punkte || 0), 0);
  const blauTotal = s.bruchtest_blau_total || (s.bruchtest_blau_scores || []).reduce((a, e) => a + (e.punkte || 0), 0);

  return (
    <div style={{ background: '#000', minHeight: '100vh', fontFamily: "'Inter','Arial',sans-serif", color: '#fff', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>

      {/* ── Winner Overlay ── */}
      {showWinner && winnerData && (
        <div onClick={() => setShowWinner(false)} style={{
          position: 'absolute', inset: 0, zIndex: 100, cursor: 'pointer',
          // Formen: kein Rot gegen Blau, also neutral (27.09.2026)
          background: isKata
            ? 'linear-gradient(135deg,rgba(40,34,18,0.97),rgba(12,10,6,0.99))'
            : winnerData.winner === 'rot'
            ? 'linear-gradient(135deg,rgba(239,68,68,0.97),rgba(160,20,20,0.99))'
            : 'linear-gradient(135deg,rgba(59,130,246,0.97),rgba(20,50,180,0.99))',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          {/* Kein Emoji auf dem Schirm (sieht auf jedem Beamer anders aus). Bei
              Formen gibt es keinen Sieger gegen einen Gegner — dort steht die
              Wertung, nicht „ROT – SIEGER“ (27.09.2026). */}
          <div style={{ fontSize: isKata ? 'clamp(2rem,6vw,4.5rem)' : 'clamp(2.5rem,10vw,7rem)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.5rem', textAlign: 'center', padding: '0 1rem', color: isKata ? '#d4af37' : undefined }}>
            {isKata ? 'WERTUNG ABGEGEBEN' : winnerData.winner === 'rot' ? 'ROT' : 'BLAU'}
          </div>
          {!isKata && <div style={{ fontSize: 'clamp(1.5rem,6vw,3.5rem)', fontWeight: 700, opacity: 0.85, marginTop: '0.3rem' }}>SIEGER</div>}

          {state?.fight_info?.[winnerData.winner] && (
            <div style={{ fontSize: 'clamp(1rem,3.5vw,2rem)', opacity: 0.8, marginTop: '1rem', textAlign: 'center', padding: '0 2rem' }}>
              {state.fight_info[winnerData.winner]}
            </div>
          )}
          {isBruchtest && (
            <div style={{ fontSize: 'clamp(1.2rem,4vw,2.5rem)', opacity: 0.7, marginTop: '1rem' }}>
              {rotTotal.toFixed(1)} : {blauTotal.toFixed(1)} Pkt
            </div>
          )}
          {isKumite && winnerData.rote_punkte !== undefined && (
            <div style={{ fontSize: 'clamp(1.2rem,4vw,2.5rem)', opacity: 0.7, marginTop: '1rem' }}>
              {winnerData.rote_punkte} : {winnerData.blaue_punkte}
            </div>
          )}
          <div style={{ marginTop: '2rem', fontSize: 'clamp(0.75rem,2vw,1rem)', opacity: 0.4 }}>Tippen zum Schließen</div>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 700, fontSize: 'clamp(0.875rem,2.5vw,1.1rem)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          ⚖️ Matte {matNr}
          {s.fight_info?.division_code && <span style={{ color: '#d9aa43', fontWeight: 400, fontSize: '0.9em' }}>{s.fight_info.division_code}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: 'clamp(0.7rem,1.5vw,0.875rem)', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{modeLabel}</span>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: connected ? '#34d399' : '#ef4444' }} />
        </div>
      </div>

      {/* ── Kämpfernamen ── */}
      {s.fight_info && (s.fight_info.rot || s.fight_info.blau) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', fontSize: 'clamp(0.75rem,2vw,1rem)', fontWeight: 600 }}>
          <div style={{ background: 'rgba(239,68,68,0.12)', padding: '0.5rem 1rem', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.04)' }}>
            <span style={{ color: '#fca5a5' }}>{s.fight_info.rot || 'ROT'}</span>
          </div>
          <div style={{ background: 'rgba(59,130,246,0.12)', padding: '0.5rem 1rem', textAlign: 'center' }}>
            <span style={{ color: '#93c5fd' }}>{s.fight_info.blau || 'BLAU'}</span>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
           KUMITE VIEW
      ══════════════════════════════════════════════════ */}
      {isKumite && (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', padding: 'clamp(0.5rem,2vw,2rem)' }}>
          {/* ROT */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(0.5rem,2vw,1rem)' }}>
            <div style={{ fontSize: 'clamp(5rem,22vw,14rem)', fontWeight: 900, color: '#ef4444', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{s.rote_punkte}</div>
            <div style={{ display: 'flex', gap: 'clamp(4px,1vw,8px)' }}>
              {[0,1,2].map(i=><div key={i} style={{ width:'clamp(12px,2.5vw,22px)', height:'clamp(12px,2.5vw,22px)', borderRadius:'50%', background:i<s.rote_warnings?'#ef4444':'rgba(255,255,255,0.1)', border:'2px solid rgba(239,68,68,0.4)' }}/>)}
            </div>
            <div style={{ fontSize:'clamp(0.875rem,2.5vw,1.5rem)', color:'#fca5a5', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em' }}>ROT</div>
          </div>
          {/* Timer */}
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'0 clamp(0.5rem,2vw,2rem)' }}>
            <div style={{ fontSize:'clamp(2rem,8vw,5rem)', fontWeight:800, letterSpacing:'-0.02em', fontVariantNumeric:'tabular-nums', color:timeRed?'#ef4444':timeYellow?'#f59e0b':'#fff', textShadow:timeRed&&s.timer_running?'0 0 30px rgba(239,68,68,0.6)':'none', transition:'color 0.3s,text-shadow 0.3s', lineHeight:1 }}>
              {formatTime(s.timer_remaining)}
            </div>
            <div style={{ width:'clamp(36px,6vw,56px)', height:2, background:s.timer_running?'#34d399':'rgba(255,255,255,0.1)', borderRadius:1, marginTop:'0.4rem', transition:'background 0.3s' }}/>
            <div style={{ fontSize:'clamp(0.6rem,1.2vw,0.875rem)', color:'rgba(255,255,255,0.25)', marginTop:'0.4rem', textTransform:'uppercase', letterSpacing:'0.08em' }}>
              {s.timer_running?'● Läuft':s.status==='active'?'⏸ Pause':'○ Bereit'}
            </div>
          </div>
          {/* BLAU */}
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'clamp(0.5rem,2vw,1rem)' }}>
            <div style={{ fontSize:'clamp(5rem,22vw,14rem)', fontWeight:900, color:'#3b82f6', lineHeight:1, fontVariantNumeric:'tabular-nums' }}>{s.blaue_punkte}</div>
            <div style={{ display:'flex', gap:'clamp(4px,1vw,8px)' }}>
              {[0,1,2].map(i=><div key={i} style={{ width:'clamp(12px,2.5vw,22px)', height:'clamp(12px,2.5vw,22px)', borderRadius:'50%', background:i<s.blaue_warnings?'#3b82f6':'rgba(255,255,255,0.1)', border:'2px solid rgba(59,130,246,0.4)' }}/>)}
            </div>
            <div style={{ fontSize:'clamp(0.875rem,2.5vw,1.5rem)', color:'#93c5fd', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em' }}>BLAU</div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
           KATA VIEW
      ══════════════════════════════════════════════════ */}
      {isKata && (
        <div style={{ flex:1, display:'flex', flexDirection:'column', padding:'clamp(0.75rem,2vw,2rem)', gap:'1rem' }}>
          <div style={{ textAlign:'center', flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
            <div style={{ fontSize:'clamp(0.875rem,2.5vw,1.2rem)', color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>{s.kata_label || 'Wertung'}</div>
            <div style={{ fontSize:'clamp(5rem,22vw,14rem)', fontWeight:900, color:'#d9aa43', lineHeight:1, fontVariantNumeric:'tabular-nums' }}>
              {s.kata_average ? parseFloat(s.kata_average).toFixed(2) : '–'}
            </div>
          </div>
          {s.kata_notes && Object.keys(s.kata_notes).length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.75rem', justifyContent:'center' }}>
              {Object.values(s.kata_notes).map((n,i) => (
                <div key={i} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16, padding:'clamp(0.5rem,2vw,1rem) clamp(0.75rem,3vw,1.5rem)', textAlign:'center' }}>
                  <div style={{ fontSize:'clamp(0.65rem,1.5vw,0.875rem)', color:'rgba(255,255,255,0.4)', marginBottom:'0.2rem' }}>{n.name}</div>
                  <div style={{ fontSize:'clamp(1.5rem,5vw,3rem)', fontWeight:800, color:'#d9aa43', lineHeight:1 }}>{(+n.note).toFixed(1)}</div>
                </div>
              ))}
            </div>
          )}
          {Object.keys(s.kata_notes||{}).length === 0 && (
            <div style={{ textAlign:'center', color:'rgba(255,255,255,0.2)', fontSize:'clamp(0.875rem,2vw,1.1rem)', padding:'2rem 0' }}>Warte auf Wertungen…</div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════
           BRUCHTEST VIEW
      ══════════════════════════════════════════════════ */}
      {isBruchtest && (
        <div style={{ flex:1, display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
          {/* ROT Seite */}
          <div style={{ borderRight:'1px solid rgba(255,255,255,0.06)', padding:'clamp(0.75rem,2vw,1.5rem)', display:'flex', flexDirection:'column' }}>
            <div style={{ textAlign:'center', marginBottom:'clamp(0.5rem,1.5vw,1rem)' }}>
              <div style={{ fontSize:'clamp(0.7rem,1.5vw,1rem)', color:'#fca5a5', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em' }}>ROT</div>
              {s.fight_info?.rot && <div style={{ fontSize:'clamp(0.75rem,1.8vw,1.1rem)', color:'rgba(255,255,255,0.6)', marginTop:'0.2rem' }}>{s.fight_info.rot}</div>}
              <div style={{ fontSize:'clamp(3rem,10vw,7rem)', fontWeight:900, color:'#ef4444', lineHeight:1, fontVariantNumeric:'tabular-nums', marginTop:'0.25rem' }}>
                {rotTotal.toFixed(1)}
              </div>
              <div style={{ fontSize:'clamp(0.65rem,1.2vw,0.875rem)', color:'rgba(255,255,255,0.3)', marginTop:'0.15rem' }}>
                {(s.bruchtest_rot_scores||[]).length} Techniken
                {s.bruchtest_active_side==='rot' && <span style={{color:'#34d399',marginLeft:'0.4rem'}}>● Aktiv</span>}
              </div>
            </div>
            <div style={{ flex:1, overflowY:'auto' }}>
              {(s.bruchtest_rot_scores||[]).map((e,i) => (
                <div key={e.id||i} style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:10, padding:'clamp(0.3rem,1vw,0.6rem) clamp(0.5rem,1.5vw,0.875rem)', marginBottom:'0.35rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.5rem' }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:'clamp(0.75rem,1.8vw,1rem)', color:'#fff' }}>{e.technikName}</div>
                      <div style={{ fontSize:'clamp(0.6rem,1.2vw,0.75rem)', color:'rgba(255,255,255,0.4)', marginTop:'0.1rem' }}>
                        {e.anzahl}× {e.materialName}{e.sprung?' 🚀':''}{e.versuche>1?` (${e.versuche}. Versuch)`:''}{!e.erfolg?' – Fehlversuch':''}
                      </div>
                      <div style={{ fontSize:'clamp(0.6rem,1.1vw,0.72rem)', color:'rgba(255,255,255,0.3)', marginTop:'0.1rem' }}>
                        S:{e.schwierigkeit} A:{e.ausfuehrung} K:{e.kraft} × ×{e.technikMult}
                      </div>
                    </div>
                    <div style={{ fontSize:'clamp(1rem,2.5vw,1.5rem)', fontWeight:800, color:e.erfolg?'#fb923c':'rgba(255,255,255,0.25)', flexShrink:0 }}>
                      {e.punkte.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
              {(s.bruchtest_rot_scores||[]).length===0 && (
                <div style={{ color:'rgba(255,255,255,0.2)', fontSize:'clamp(0.75rem,1.5vw,0.875rem)', textAlign:'center', padding:'1rem 0' }}>Noch keine Techniken</div>
              )}
            </div>
          </div>

          {/* BLAU Seite */}
          <div style={{ padding:'clamp(0.75rem,2vw,1.5rem)', display:'flex', flexDirection:'column' }}>
            <div style={{ textAlign:'center', marginBottom:'clamp(0.5rem,1.5vw,1rem)' }}>
              <div style={{ fontSize:'clamp(0.7rem,1.5vw,1rem)', color:'#93c5fd', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em' }}>BLAU</div>
              {s.fight_info?.blau && <div style={{ fontSize:'clamp(0.75rem,1.8vw,1.1rem)', color:'rgba(255,255,255,0.6)', marginTop:'0.2rem' }}>{s.fight_info.blau}</div>}
              <div style={{ fontSize:'clamp(3rem,10vw,7rem)', fontWeight:900, color:'#3b82f6', lineHeight:1, fontVariantNumeric:'tabular-nums', marginTop:'0.25rem' }}>
                {blauTotal.toFixed(1)}
              </div>
              <div style={{ fontSize:'clamp(0.65rem,1.2vw,0.875rem)', color:'rgba(255,255,255,0.3)', marginTop:'0.15rem' }}>
                {(s.bruchtest_blau_scores||[]).length} Techniken
                {s.bruchtest_active_side==='blau' && <span style={{color:'#34d399',marginLeft:'0.4rem'}}>● Aktiv</span>}
              </div>
            </div>
            <div style={{ flex:1, overflowY:'auto' }}>
              {(s.bruchtest_blau_scores||[]).map((e,i) => (
                <div key={e.id||i} style={{ background:'rgba(59,130,246,0.07)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:10, padding:'clamp(0.3rem,1vw,0.6rem) clamp(0.5rem,1.5vw,0.875rem)', marginBottom:'0.35rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.5rem' }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:'clamp(0.75rem,1.8vw,1rem)', color:'#fff' }}>{e.technikName}</div>
                      <div style={{ fontSize:'clamp(0.6rem,1.2vw,0.75rem)', color:'rgba(255,255,255,0.4)', marginTop:'0.1rem' }}>
                        {e.anzahl}× {e.materialName}{e.sprung?' 🚀':''}{e.versuche>1?` (${e.versuche}. Versuch)`:''}{!e.erfolg?' – Fehlversuch':''}
                      </div>
                      <div style={{ fontSize:'clamp(0.6rem,1.1vw,0.72rem)', color:'rgba(255,255,255,0.3)', marginTop:'0.1rem' }}>
                        S:{e.schwierigkeit} A:{e.ausfuehrung} K:{e.kraft} × ×{e.technikMult}
                      </div>
                    </div>
                    <div style={{ fontSize:'clamp(1rem,2.5vw,1.5rem)', fontWeight:800, color:e.erfolg?'#fb923c':'rgba(255,255,255,0.25)', flexShrink:0 }}>
                      {e.punkte.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
              {(s.bruchtest_blau_scores||[]).length===0 && (
                <div style={{ color:'rgba(255,255,255,0.2)', fontSize:'clamp(0.75rem,1.5vw,0.875rem)', textAlign:'center', padding:'1rem 0' }}>Noch keine Techniken</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{ background:'rgba(255,255,255,0.03)', borderTop:'1px solid rgba(255,255,255,0.05)', padding:'0.4rem 1rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
        <div style={{ fontSize:'clamp(0.65rem,1.2vw,0.8rem)', color:'rgba(255,255,255,0.25)', textTransform:'uppercase' }}>
          {s.status==='active'?'● Aktiv':s.status==='finished'?'✓ Beendet':'○ Bereit'}
        </div>
        {s.connected_referees?.filter(r=>r.role!=='display').length > 0 && (
          <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', justifyContent:'flex-end' }}>
            {s.connected_referees.filter(r=>r.role!=='display').map((r,i) => (
              <span key={i} style={{ fontSize:'clamp(0.6rem,1.1vw,0.75rem)', padding:'0.15rem 0.5rem', borderRadius:6, background:r.role==='haupt'?'rgba(215,162,58,0.15)':'rgba(255,255,255,0.05)', color:r.role==='haupt'?'#d9aa43':'rgba(255,255,255,0.35)' }}>
                {r.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
